/**
 * The civic horizon behind the entry screen: Parliament House, drawn freehand.
 *
 * Drawn in the tiles' own hand rather than to a ruler - same stroke ink, round
 * caps and joins, comparable weight, and no straight line anywhere in it. A
 * ruled architectural elevation sat in a different world from the hand-cut
 * paper blocks and read as a wireframe parked behind them.
 *
 * Wide and low on purpose: this is horizon, not subject. It holds the bottom
 * of the canvas at roughly a tenth of full ink so the artwork above it and the
 * words beside it both stay first.
 *
 * Pinned to the VIEWPORT, not to the document. The sign-in column is taller
 * than a short window, so a document-anchored horizon scrolls off the bottom
 * and stops being a horizon at exactly the window sizes the shell allows.
 *
 * Decorative to assistive tech, exactly like the tile artwork and the logo
 * SVGs. Only the flag cloth moves, and it moves in CSS, so no render keeps it
 * going and `prefers-reduced-motion` stills it.
 */

/** Roof lines, wings, the central block and the flagpole mast. */
const MAJOR_PATHS = [
  // The horizon itself, bowed across the full width.
  'M40 248 C 420 244 900 242 1564 247',
  // long low wings
  'M300 247 C 299 216 300 208 302 204 C 400 199 500 200 592 204 C 594 216 593 234 592 247',
  'M1008 247 C 1007 216 1008 208 1010 204 C 1102 199 1202 200 1300 204 C 1302 216 1301 234 1300 247',
  // the curved retaining walls sweeping up to the centre
  'M592 204 C 650 202 680 186 697 162',
  'M1008 204 C 950 202 920 186 903 162',
  // central block, then the block above it
  'M697 247 C 696 178 697 167 699 161 C 790 155 810 155 901 161 C 903 178 904 216 902 247',
  'M741 247 C 740 191 741 183 743 178 C 800 174 800 174 857 178 C 859 191 860 217 858 247',
  // the flagpole: mast, then its four splayed legs
  'M800 161 C 799 120 800 70 800 30',
  'M761 161 C 773 124 786 78 798 36',
  'M839 161 C 827 124 814 78 802 36',
] as const;

/** Distant buildings, colonnade, steps - the lighter second weight. */
const MINOR_PATHS = [
  // distant buildings, holding the horizon at each end
  'M92 246 C 91 226 92 220 94 217 C 132 214 170 214 208 217 C 210 226 209 238 208 246',
  'M112 232 C 122 231 132 231 142 232',
  'M1392 246 C 1391 224 1392 218 1394 215 C 1432 212 1470 212 1508 215 C 1510 224 1509 238 1508 246',
  'M1414 230 C 1424 229 1434 229 1444 230',
  'M340 247 C 339 228 340 220 341 213',
  'M1260 247 C 1259 228 1260 220 1261 213',
  // colonnade
  'M756 243 C 755 224 756 208 757 189',
  'M774 243 C 773 224 774 208 775 188',
  'M792 243 C 791 224 792 208 793 189',
  'M810 243 C 809 224 810 208 810 188',
  'M828 243 C 827 224 828 208 829 189',
  'M846 243 C 845 224 846 208 846 188',
  // forecourt steps
  'M658 247 C 760 243 840 243 942 247',
  'M692 239 C 780 236 820 236 908 239',
  // the inner pair of flagpole legs
  'M778 161 C 786 126 793 80 799 34',
  'M822 161 C 814 126 807 80 801 34',
] as const;

/** The cloth itself, closed so it reads as fabric rather than as more linework. */
const FLAG_PATH = 'M804 34 C 826 29 846 38 868 32 C 869 44 868 54 869 61 C 847 67 826 57 804 62 Z';

export function DesktopEntrySkyline() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 bottom-0 w-full text-entry-stroke opacity-10"
      data-testid="desktop-entry-skyline"
      fill="none"
      focusable="false"
      viewBox="0 0 1600 260"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.4}>
        {MAJOR_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4}>
        {MINOR_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
        <g className="pp-entry-flag">
          <path d={FLAG_PATH} />
        </g>
      </g>
    </svg>
  );
}
